import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, LogOut, Loader2, ShieldCheck } from "lucide-react";
import { useTenant } from "../../../contexts/TenantContext";
import { rmapi, getRmToken, setRmToken, apiErr } from "../../../lib/tenant";
import { inputCls, labelCls, BrandButton } from "../TenantShell";

const RMContext = createContext(null);
export const useRM = () => useContext(RMContext);

export function RMProvider({ children }) {
  const { sub } = useTenant();
  const [rm, setRm] = useState(undefined);
  const refresh = useCallback(async () => {
    if (!getRmToken()) { setRm(null); return null; }
    try { const { data } = await rmapi.get(`/t/${sub}/rm/me`); setRm(data); return data; }
    catch { setRmToken(null); setRm(null); return null; }
  }, [sub]);
  useEffect(() => { refresh(); }, [refresh]);
  const logout = useCallback(() => { setRmToken(null); setRm(null); }, []);
  const value = useMemo(() => ({ rm, refresh, logout, setRm }), [rm, refresh, logout]);
  return <RMContext.Provider value={value}>{children}</RMContext.Provider>;
}

export const RMShell = ({ children, title }) => {
  const { config, brand, logoSrc, to } = useTenant();
  const { rm, logout } = useRM();
  return (
    <div className="tenant-portal min-h-screen bg-[#F7F8FA] text-neutral-900" style={{ "--tenant": brand }} data-testid="rm-shell">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <Link to={to("/rm")} className="flex items-center gap-3" data-testid="rm-header-logo">
            {logoSrc ? <img src={logoSrc} alt={config.name} className="h-9 max-w-[150px] object-contain" />
              : <span className="flex h-9 w-9 items-center justify-center rounded-lg text-white" style={{ background: brand }}><Building2 size={18} /></span>}
            <span className="text-sm font-bold">{!logoSrc && config.name} <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: brand }}>RM portal</span></span>
          </Link>
          {rm && (
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              <span className="hidden sm:inline" data-testid="rm-header-user">{rm.name} · {rm.email}</span>
              <button type="button" onClick={logout} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="rm-logout"><LogOut size={12} /> Sign out</button>
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-8">{title && <h1 className="mb-5 text-2xl font-extrabold tracking-tight" data-testid="rm-page-title">{title}</h1>}{children}</main>
    </div>
  );
};

export function RMLogin() {
  const { sub, config } = useTenant();
  const { refresh } = useRM();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await rmapi.post(`/t/${sub}/rm/auth/login`, f); setRmToken(data.token); await refresh(); }
    catch (err) { toast.error(apiErr(err, "Couldn't sign you in")); }
    setBusy(false);
  };
  return (
    <RMShell>
      <form onSubmit={submit} className="mx-auto mt-6 max-w-sm space-y-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="rm-login-card">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: "var(--tenant)" }}><ShieldCheck size={20} /></span>
        <div><h1 className="text-xl font-extrabold">Relationship Manager sign in</h1><p className="text-sm text-neutral-500">For {config.name} staff only. Accounts are issued by Clinq.</p></div>
        <label className="block"><span className={labelCls}>Email</span><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="rm-login-email" /></label>
        <label className="block"><span className={labelCls}>Password</span><input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="rm-login-password" /></label>
        <BrandButton type="submit" disabled={busy} className="w-full" data-testid="rm-login-submit">{busy && <Loader2 size={14} className="animate-spin" />} Sign in</BrandButton>
      </form>
    </RMShell>
  );
}

export function RMForcePassword() {
  const { sub } = useTenant();
  const { refresh } = useRM();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try { await rmapi.post(`/t/${sub}/rm/auth/change-password`, { current_password: f.current_password, new_password: f.new_password }); toast.success("Password updated"); await refresh(); }
    catch (err) { toast.error(apiErr(err, "Couldn't change password")); }
    setBusy(false);
  };
  return (
    <RMShell>
      <form onSubmit={submit} className="mx-auto mt-6 max-w-sm space-y-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="rm-force-password">
        <div><h1 className="text-xl font-extrabold">Choose a new password</h1><p className="text-sm text-neutral-500">You signed in with a temporary password. Set your own to continue.</p></div>
        <label className="block"><span className={labelCls}>Temporary password</span><input required type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={inputCls} data-testid="rm-pw-current" /></label>
        <label className="block"><span className={labelCls}>New password</span><input required type="password" minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={inputCls} data-testid="rm-pw-new" /></label>
        <label className="block"><span className={labelCls}>Confirm new password</span><input required type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={inputCls} data-testid="rm-pw-confirm" /></label>
        <BrandButton type="submit" disabled={busy} className="w-full" data-testid="rm-pw-submit">{busy && <Loader2 size={14} className="animate-spin" />} Save password</BrandButton>
      </form>
    </RMShell>
  );
}

export function RMGate({ children }) {
  const { rm } = useRM();
  const navigate = useNavigate();
  useEffect(() => { /* keep hook order stable */ }, [navigate]);
  if (rm === undefined) return <div className="tenant-portal flex min-h-screen items-center justify-center bg-[#F7F8FA]"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  if (rm === null) return <RMLogin />;
  if (rm.must_change_password) return <RMForcePassword />;
  return children;
}
